import { creditsProgress } from '../../../../lib/credits-bar-color';
import { useBasePath } from '../../../../lib/use-base-path';
import { resolveCreditsMeter, useCredits } from '../../../../lib/use-credits';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useTranslation } from '../../../../lib/i18n';
import { usePlans, usePremium } from '../../../settings/shared/settings-queries';
import { activePlanIsMaxPlus } from '../../../settings/shared/max-plus';
import { activePremiumSubscription } from '../../../settings/shared/settings-plan';
import { planNameKeyOf } from '../../../settings/shared/settings-shared';
import type { ProgressLevel } from '../../ui';

export interface CreditsSummary {
  planLabel: string;
  /** "70 of 150 monthly credits used" — already localized and number-formatted. */
  usageLabel: string;
  /** Credits SPENT as a 0–1 fraction of the cap, for the bar's width — so the bar fills
   *  as the allowance is consumed, matching the settings meter and the native apps. */
  fraction: number;
  /** The DS Progress Bar level — see `creditsProgress`: success, then pending, then destructive. */
  progress: ProgressLevel;
  /** The card BODY opens the Account page (the credits/settings screen). */
  account: { route: string; path: string; label: string; onClick: () => void };
  /** The Upgrade button opens the package picker — the SAME destination as the
   *  Account page's own Upgrade button, matching iOS/Android's `CreditsUpgrade`. */
  upgrade: { route: string; path: string; label: string; onClick: () => void };
  title: string;
}

// The credits card's numbers and words: plan name, usage line, how full the bar is
// and which level it is at. Returns null while the balance is unknown or the caller
// has no metasite — there is nothing honest to show then. Shared by the wide rail's
// card and the mobile drawer's.
export function useCreditsSummary(): CreditsSummary | null {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const credits = useCredits();
  // The Max+ signal for the drawer's CTA (Change vs Upgrade). The sidebar persists across SPA
  // navigations, so these two reads fetch once per load and stay cached — no per-page cost. Using
  // the SAME catalog rule as the account card (activePlanIsMaxPlus) keeps the two surfaces in step.
  const { data: premium = null } = usePremium();
  const { data: plans = null } = usePlans();
  const { loading, failed, hasMetaSite, account } = credits;
  if (loading || failed || hasMetaSite === false || !account) return null;

  const isMaxPlus = activePlanIsMaxPlus(premium, plans);

  const { isFreePlan } = account;
  // The plan's own NAME, exactly as both native drawers read it (`creditsPlanDisplayName`): the catalog
  // name of the active subscription. Web used to say a generic "Paid Plan" here while the phones named
  // the plan — so one account read "Paid Plan" in the menu and "Max" on the account page. Falls back to
  // the account card's own generic label when the plan left the catalog (native falls back to the FREE
  // label, which would misreport a paying user).
  const paidPlanName = t(planNameKeyOf(plans, activePremiumSubscription(premium)?.planId ?? null) ?? 'settings.plan.yourPlan');
  // No meter means the balance is unknown, and the card is nothing but the balance — so it stays away
  // rather than reading "0 of 0 monthly credits used", which is what an exhausted account looks like.
  const meter = resolveCreditsMeter(credits);
  if (!meter) return null;
  const { cap, used } = meter;
  const fraction = cap > 0 ? Math.min(1, Math.max(0, used / cap)) : 0;
  const progress = creditsProgress(fraction);

  return {
    planLabel: isFreePlan ? t('sidebar.credits.freePlan') : paidPlanName,
    usageLabel: t('sidebar.credits.used', { used: used.toLocaleString(), cap: cap.toLocaleString() }),
    fraction,
    progress,
    title: t('sidebar.credits'),
    account: {
      route: '/settings',
      path: `${basePath}/settings`,
      label: t('nav.settings'),
      onClick: () => {
        // The card body goes to Account, not Upgrade — mirrors native's `drawer_credits_card`
        // tap so the three surfaces' menu funnels join.
        logPageClickBi({ page_name: 'menu', button_name: 'drawer_credits_card', origin: 'menu' });
        leaveChat();
      },
    },
    upgrade: {
      route: '/packages',
      path: `${basePath}/packages`,
      // The top tier reads "Switch" — there is nothing above it to upgrade TO — and every other
      // plan, paid or free, reads "Upgrade". One rule, shared with both Settings surfaces.
      label: isMaxPlus ? t('settings.plan.switchPlan') : t('sidebar.credits.upgrade'),
      onClick: () => {
        markNavFromDrawer();
        // The four fields the BI review asked for on this click: where Upgrade goes, which plan the
        // user is leaving, and the credit position they leave it at. None has a registered src:172
        // field, so they ride `additionalFields` — same four keys as the native apps'
        // `upgrade_credits`, so the three surfaces' upgrade funnels join.
        logPageClickBi(
          { page_name: 'menu', button_name: 'upgrade_credits', origin: 'menu' },
          {
            additionalFields: {
              url: `${basePath}/packages`,
              plan: isFreePlan ? 'free' : 'paid',
              plan_credits: String(cap),
              remain_credits: String(Math.max(0, cap - used)),
            },
          },
        );
        leaveChat();
      },
    },
  };
}
